import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { translate } from '@/services/TranslationService';
import { dispatch } from '@/services/store';
import { Typography } from '@/components/novoville';
import { toast } from '@/components/ui/use-toast';
import VerifyModal from '../modal/VerifyModal';
import { withNOVOHeader } from '../sideMenu/HeaderHOC';
import RFInput from './RFInput';
import { getPaymentDetails, getPublicPaymentDetails } from './actions';
import ReCAPTCHAInput from '../auth/components/ReCAPTCHAInput';

const isTranslationMissing = (translationKey) => {
  const translation = translate(translationKey);
  return translation === translationKey;
}

const PROMPT_KEY_BASE = 'paymentprompt';


const Payments = (props) => {
  const { setHeaderTitle } = props;

  const { isPublic = false, preCompletedRF = '' } = props;

  const formRef = React.createRef();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { hasVerifiedEmail, hasVerifiedPhoneNumber } = useSelector((state) => ({
    hasVerifiedEmail: state.profileReducer.hasVerifiedEmail,
    hasVerifiedPhoneNumber: state.profileReducer.hasVerifiedPhoneNumber,
  }));

  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const isVerified = hasVerifiedEmail && hasVerifiedPhoneNumber;

  useEffect(() => {
    setHeaderTitle(translate('sideMenu.Payments'));
  }, []);

  const [rFInputValue, setRFInputValue] = useState(preCompletedRF || '');
  const [isRFError, setIsRFError] = useState(false);

  const checkRFValidation = () => {
    let isValid = true;

    if (rFInputValue.length < 5) isValid = false;

    setIsRFError(!isValid);

    return isValid;
  };

  const onRFInputChange = (value) => {
    setIsRFError(false);
    setRFInputValue(value);
  };

  const openModalIfNotVerified = () => {
    if (isVerified === false) {
      setIsModalOpen(true);
      return false;
    }

    setIsModalOpen(false);

    return true;
  };

  const onFindClick = async () => {
    if (!openModalIfNotVerified() && !isPublic) return;

    if (isRFError) return;

    const isValid = checkRFValidation();
    if (!isValid) return;

    if (isPublic && !recaptchaToken) {
      await toast({
        title: translate('OTHER.errorOcurred'),
        description: (translate('PublicPaymentsRecaptchaRequired')),
        variant: 'destructive',
        duration: 5000,
        action: null,
      });
      return;
    }

    try {
      const result = await dispatch(
        isPublic
          ? getPublicPaymentDetails(rFInputValue, recaptchaToken)
          : getPaymentDetails(rFInputValue)
      );

      const targetPath = isPublic
        ? `/public/payments/${rFInputValue}`
        : `/payments/${rFInputValue}`;

      props.history.push(targetPath, {
        paymentDetails: result,
        recaptchaToken: isPublic ? recaptchaToken : undefined,
      });
    } catch (error) {
      await toast({
        title: translate('OTHER.errorOcurred'),
        description: translate('PaymentCodeNotFoundLong'),
        variant: 'destructive',
        duration: 5000,
        action: null,
      });
      setIsRFError(true);
    }
  };

  const authorityName = useSelector((state) => state.profileReducer?.profile?.authority?.name);
  const authorityPromptKey = authorityName ? `${authorityName}_${PROMPT_KEY_BASE}` : null;
  const defaultPromptKey = PROMPT_KEY_BASE;
  const fallbackPromptKey = 'EnterRFCode';

  let promptKey = fallbackPromptKey;

  if (authorityPromptKey && !isTranslationMissing(authorityPromptKey)) {
    promptKey = authorityPromptKey;
  }

  if (promptKey === fallbackPromptKey && !isTranslationMissing(defaultPromptKey)) {
    promptKey = defaultPromptKey;
  }

  console.log('Using prompt key:', promptKey);
  const subtextOfPayment = translate(promptKey);

  return (
    <div className="flex flex-1 sm:flex-grow-0 flex-col relative">
      <div className="flex flex-col sm:m-10 m-0 sm:max-h-[calc(100vh-10rem)] sm:py-8 py-2 sm:px-8 px-4 flex-1 h-fit bg-white rounded-sm">
        <div className="flex flex-1 flex-col gap-6">
          <Typography variant={'bold_24'}>
            {translate('MakeAPayment')}
          </Typography>
          <Typography variant={'regular_16'} className="text-gray-700 mb-2">
            {subtextOfPayment}
          </Typography>
          <form
            className="flex flex-1 flex-col gap-4 w-full"
            ref={formRef}
            onSubmit={(e) => {
              e.preventDefault();
              onFindClick();
            }}
          >
            <div className="flex-grow sm:flex-grow-0">
              <RFInput
                value={rFInputValue}
                onChange={onRFInputChange}
                onBlur={checkRFValidation}
                error={isRFError}
              />
            </div>
            <div>
              <VerifyModal
                onClick={() => {
                  formRef.current?.requestSubmit();
                }}
                className="h-12 sm:mt-4 sm:max-w-36 mt-auto mx-auto sm:mx-0"
                onOpenChange={setIsModalOpen}
                shouldOpen={isModalOpen && !isVerified && !isPublic}
              >
                {translate('FindPayment')}
              </VerifyModal>
            </div>
            {
              isPublic && (
                <div className="mt-4">
                  <ReCAPTCHAInput
                    onVerify={(token) => {
                      setRecaptchaToken(token);
                    }}
                  />
                </div>
              )
            }
            <a
              href={translate('paymentTermsUrl')}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2"
            >
              <Typography
                variant="regular_14"
                className="text-gray-500 hover:underline cursor-pointer hover:text-gray-700"
              >
                {translate('seeTheTerms')}
              </Typography>
            </a>

          </form>
        </div>
      </div>
    </div>
  );
};

export default withNOVOHeader(withRouter(Payments));
